import { FunctionComponent, memo, useCallback, useContext, useMemo, useState } from 'react';
import { ConfigContext } from '../../context';
import { InAppActiveMessage } from '../../services';
import { Modal, parseInAppMessageAction, ShareUrlModal, useAnalytics } from '../../shared';

import classNames from 'classnames';
import classes from './InAppMessage.module.scss';

interface InAppMessageProps {
  isOpen: boolean;
  message: InAppActiveMessage;
  onClose: () => void | Promise<void>;
}

export const InAppMessage: FunctionComponent<InAppMessageProps> = memo(
  ({ message, isOpen, onClose }) => {
    const { logInAppMessageView } = useAnalytics();

    const { organisationLogoUrl } = useContext(ConfigContext).config.elements;

    const { id, title, intro, textColor, bgColor, primary, image } = message;
    const {
      text: buttonText,
      textColor: buttonTextColor,
      bgColor: buttonBgColor,
      action,
    } = primary;

    const [shareUrl, setShareUrl] = useState<string>('');

    const buttonClickHandler = useCallback(async () => {
      logInAppMessageView({
        iam_id: id,
        iam_action: 'primary',
        time_stamp: new Date().toISOString(),
      });

      const { isShare, isOpenUrl, url } = parseInAppMessageAction(action);

      if (url && isShare) {
        setShareUrl(url);
        onClose();
        return;
      }

      //return auto overflow for body via custom button click
      //usually it works via standard modal close button or Accept/Cancel buttons
      document.body.style.overflow = 'auto';

      if (url && isOpenUrl) {
        await onClose();
        window.location.assign(url);
        return;
      }

      onClose();

      //cancel action
      if (!url) {
        return;
      }

      window.open(url, '_blank', 'noreferrer');
    }, [action, id, logInAppMessageView, onClose]);

    const body = useMemo(
      () => (
        <div className={classes['body']}>
          <img
            className={classNames(classes['body__image'], {
              [classes['body__image--logo']]: !image?.url,
            })}
            {...(image && { width: image.width })}
            src={image?.url ?? organisationLogoUrl}
            alt={''}
          />
          <div
            className={classes['body__text']}
            style={{
              color: textColor,
            }}
          >
            <div className={classes['body__title']}>{title}</div>
            <div className={classes['body__intro']}>{intro}</div>
          </div>
          <button
            className={classes['body__button']}
            onClick={buttonClickHandler}
            style={{
              backgroundColor: buttonBgColor,
              color: buttonTextColor,
            }}
          >
            {buttonText}
          </button>
        </div>
      ),
      [
        buttonBgColor,
        buttonClickHandler,
        buttonText,
        buttonTextColor,
        image,
        intro,
        organisationLogoUrl,
        textColor,
        title,
      ]
    );

    return (
      <>
        <Modal
          isOpen={isOpen}
          body={body}
          onClose={onClose}
          contentStyle={{ backgroundColor: bgColor }}
          ariaLabel={title}
        />

        <ShareUrlModal isOpen={Boolean(shareUrl)} url={shareUrl} onClose={() => setShareUrl('')} />
      </>
    );
  }
);
